import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from '../Icon/Icon.js';
import Text from '../Text/Text.js';
import { isReactNodeEmpty } from '../../utils/react.js';
import { getIntentClass, CommonClasses } from '../../commonClasses.js';

const BASE_CLASS = 'nui-tag';
const tagPropTypes = {
  /** Whether the tag should appear in an active state. */
  active: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  /** Whether the tag should take up the full width of its container. */
  fill: PropTypes.bool,
  /** HTML title to be passed to the <Text> component */
  htmlTitle: PropTypes.string,
  /** Name of a nui icon (or an icon element) to render before the children. */
  icon: PropTypes.string,
  intent: PropTypes.string,
  /**
   * Whether the tag should visually respond to user interactions. If set
   * to `true`, hovering over the tag will change its color and mouse cursor.
   * Recommended when `onClick` is also defined.
   */
  interactive: PropTypes.bool,
  /** Whether this tag should use large styles. */
  large: PropTypes.bool,
  /**
   * Whether tag content should be allowed to occupy multiple lines.
   * If false, a single line of text will be truncated with an ellipsis if
   * it overflows. Note that icons will be vertically centered relative to
   * multiline text.
   */
  multiline: PropTypes.bool,
  /**
   * Callback invoked when the tag is clicked.
   * Recommended when `interactive` is `true`.
   */
  onClick: PropTypes.func,
  /** Name of a nui icon (or an icon element) to render after the children. */
  rightIcon: PropTypes.string,
  /** Whether this tag should have rounded ends. */
  round: PropTypes.bool,
  /** Whether this tag should use small styles. */
  small: PropTypes.bool,
  tabIndex: PropTypes.number
};
const Tag = /*#__PURE__*/forwardRef(({
  active = false,
  children,
  className,
  fill = false,
  htmlTitle,
  icon,
  intent = 'primary',
  interactive = false,
  large = false,
  multiline = false,
  rightIcon,
  round = false,
  tabIndex = 0,
  small = false,
  ...htmlProps
}, ref) => {
  const tagClasses = cx(BASE_CLASS, getIntentClass(intent), {
    [CommonClasses.ACTIVE]: active,
    [CommonClasses.FILL]: fill,
    [CommonClasses.INTERACTIVE]: interactive,
    [CommonClasses.LARGE]: large,
    [CommonClasses.ROUND]: round,
    [CommonClasses.SMALL]: small
  }, className);
  const maybeRenderIcon = () => typeof icon === 'string' ? /*#__PURE__*/React__default.createElement(Icon, {
    name: icon
  }) : icon;
  const maybeRenderRightIcon = () => typeof rightIcon === 'string' ? /*#__PURE__*/React__default.createElement(Icon, {
    name: rightIcon
  }) : rightIcon;
  return /*#__PURE__*/React__default.createElement("span", _extends({}, htmlProps, {
    className: tagClasses,
    tabIndex: interactive ? tabIndex : undefined,
    ref: ref
  }), maybeRenderIcon(), !isReactNodeEmpty(children) && /*#__PURE__*/React__default.createElement(Text, {
    className: CommonClasses.FILL,
    ellipsized: !multiline,
    title: htmlTitle
  }, children), maybeRenderRightIcon());
});
Tag.propTypes = tagPropTypes;
Tag.displayName = 'NuiTag';
var Tag$1 = Tag;

export { Tag$1 as default };
